import { useMemo, useState, type ReactNode } from 'react';
import { ShieldCheck, EyeOff } from 'lucide-react';
import { Button } from '../ui';

interface ScrubbedPayloadPaneProps {
  scrubbedTranscript: string;
  entityMap: Record<string, string>;
  /** Called with the analyst's text selection to tokenize a missed value. */
  onTokenize?: (value: string) => Promise<void> | void;
  tokenizing?: boolean;
}

const TOKEN_PATTERN = /\b(?:URL|PATH|USER|IP|HASH|DOMAIN|HOST|ENT)_\d+\b/g;

/**
 * Shows the EXACT text that is (or will be) sent to the drafting model, with
 * entity tokens highlighted. This is the trust-boundary review surface: if a
 * hostname or indicator slipped past the scrubber, the analyst selects it and
 * clicks "Redact selection" — the transcript is re-scrubbed and any existing
 * draft is invalidated.
 */
export function ScrubbedPayloadPane({
  scrubbedTranscript,
  entityMap,
  onTokenize,
  tokenizing,
}: ScrubbedPayloadPaneProps) {
  const [selection, setSelection] = useState('');

  const rendered = useMemo(() => {
    const nodes: ReactNode[] = [];
    const lines = scrubbedTranscript.split('\n');
    lines.forEach((line, lineIdx) => {
      let last = 0;
      for (const match of line.matchAll(TOKEN_PATTERN)) {
        const idx = match.index ?? 0;
        if (idx > last) nodes.push(line.slice(last, idx));
        const token = match[0];
        nodes.push(
          <mark
            key={`${lineIdx}-${idx}`}
            title={entityMap[token] ? `Redacted: ${entityMap[token]}` : token}
            className="bg-accent-yellow/20 text-accent-yellow font-mono text-[0.85em] px-0.5 rounded"
          >
            {token}
          </mark>
        );
        last = idx + token.length;
      }
      if (last < line.length) nodes.push(line.slice(last));
      nodes.push('\n');
    });
    return nodes;
  }, [scrubbedTranscript, entityMap]);

  const handleMouseUp = () => {
    const text = window.getSelection()?.toString().trim() ?? '';
    setSelection(text.length >= 2 && text.length <= 200 && !text.includes('\n') ? text : '');
  };

  const tokenCount = Object.keys(entityMap).length;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border-default bg-bg-secondary rounded-t-lg">
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          <ShieldCheck className="w-4 h-4 text-accent-green" />
          <span>
            Payload sent to the model — {tokenCount} redacted value{tokenCount === 1 ? '' : 's'}
          </span>
        </div>
        {onTokenize && (
          <Button
            size="sm"
            variant="secondary"
            disabled={!selection || tokenizing}
            onClick={async () => {
              if (selection) {
                await onTokenize(selection);
                setSelection('');
                window.getSelection()?.removeAllRanges();
              }
            }}
          >
            <EyeOff className="w-3.5 h-3.5 mr-1" />
            {tokenizing ? 'Redacting…' : selection ? `Redact "${selection.slice(0, 24)}${selection.length > 24 ? '…' : ''}"` : 'Redact selection'}
          </Button>
        )}
      </div>
      <pre
        onMouseUp={handleMouseUp}
        className="flex-1 min-h-0 overflow-auto p-3 text-xs leading-relaxed whitespace-pre-wrap font-mono text-text-primary bg-bg-primary border border-t-0 border-border-default rounded-b-lg"
      >
        {rendered}
      </pre>
    </div>
  );
}
